import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Shield, Check, ChevronLeft, CreditCard, Loader, Wallet, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const BILLING_LABELS = { monthly: 'Monthly', quarterly: 'Every 3 months', annually: 'Annually' };

const OrderPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const billing = searchParams.get('billing') || 'monthly';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState('stripe');
  const [serverName, setServerName] = useState('');
  const [serverType, setServerType] = useState('paper');
  const [balance, setBalance] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // { code, percent_off }
  const [checkingCoupon, setCheckingCoupon] = useState(false);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCheckingCoupon(true);
    try {
      const { data } = await api.post('/coupons/validate', { code: couponInput.trim() });
      setCoupon(data);
      toast.success(`Coupon applied: ${data.percent_off}% off!`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.error || 'Invalid coupon');
    } finally {
      setCheckingCoupon(false);
    }
  };

  const discountedTotal = () => {
    const base = getPrice();
    if (!coupon) return base;
    return Math.max(0, parseFloat((base * (100 - coupon.percent_off) / 100).toFixed(2)));
  };

  useEffect(() => {
    api.get(`/plans/${slug}`).then(({ data }) => {
      setPlan(data.plan);
      setServerName(`${user?.first_name}'s Server`);
    }).catch(() => navigate('/pricing')).finally(() => setLoading(false));

    // Fetch customer balance
    api.get('/balance').then(({ data }) => setBalance(data.balance)).catch(() => {});
  }, [slug, navigate, user]);

  const getPrice = () => {
    if (!plan) return 0;
    if (billing === 'annually' && plan.price_annually) return parseFloat(plan.price_annually);
    if (billing === 'quarterly' && plan.price_quarterly) return parseFloat(plan.price_quarterly);
    return parseFloat(plan.price_monthly);
  };

  const getMonthlyEquivalent = () => {
    if (!plan) return 0;
    if (billing === 'annually' && plan.price_annually) return (parseFloat(plan.price_annually) / 12).toFixed(2);
    if (billing === 'quarterly' && plan.price_quarterly) return (parseFloat(plan.price_quarterly) / 3).toFixed(2);
    return parseFloat(plan.price_monthly).toFixed(2);
  };

  const getSavings = () => {
    if (!plan) return null;
    if (billing === 'annually' && plan.price_annually) {
      const saved = ((parseFloat(plan.price_monthly) * 12) - parseFloat(plan.price_annually)).toFixed(2);
      return `You save €${saved} vs monthly`;
    }
    if (billing === 'quarterly' && plan.price_quarterly) {
      const saved = ((parseFloat(plan.price_monthly) * 3) - parseFloat(plan.price_quarterly)).toFixed(2);
      return `You save €${saved} vs monthly`;
    }
    return null;
  };

  const handleStripe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post('/payments/stripe/create-session', {
        plan_id: plan.id,
        billing_cycle: billing,
        server_name: serverName,
        server_type: serverType,
        coupon_code: coupon?.code || undefined,
      });
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start checkout');
      setPaying(false);
    }
  };

  const handleBalance = async () => {
    setPaying(true);
    try {
      const { data } = await api.post('/balance/pay-order', {
        plan_id: plan.id,
        billing_cycle: billing,
        server_name: serverName,
        server_type: serverType,
        coupon_code: coupon?.code || undefined,
      });
      toast.success(`Payment successful! €${data.amount_paid.toFixed(2)} deducted from balance.`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Balance payment failed');
      setPaying(false);
    }
  };

  const handlePay = () => {
    if (!serverName.trim() || serverName.trim().length < 3) {
      toast.error('Server name must be at least 3 characters');
      return;
    }
    if (payMethod === 'stripe') handleStripe();
    else if (payMethod === 'balance') handleBalance();
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <Loader className="w-8 h-8 text-green-400 animate-spin" />
    </div>
  );

  if (!plan) return null;

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/pricing" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to pricing
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Left - Order form */}
          <div className="md:col-span-3 space-y-6">
            <div>
              <h1 className="text-3xl font-black text-white mb-1">Complete your order</h1>
              <p className="text-gray-400">You're getting the <span className="text-green-400 font-semibold">{plan.name}</span> plan</p>
            </div>

            {/* Server config */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-bold text-white">Server setup</h2>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Server name</label>
                <input
                  type="text"
                  value={serverName}
                  onChange={(e) => setServerName(e.target.value)}
                  maxLength={64}
                  className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors"
                  placeholder="My Minecraft Server"
                />
                <p className="text-gray-500 text-xs mt-1">You can rename this anytime from your dashboard</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Server type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'paper', label: 'Paper', desc: 'Best performance + plugins' },
                    { value: 'vanilla', label: 'Vanilla', desc: 'Pure Minecraft experience' },
                    { value: 'forge', label: 'Forge', desc: 'Mod support' },
                    { value: 'fabric', label: 'Fabric', desc: 'Lightweight mods' },
                  ].map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setServerType(t.value)}
                      className={`p-3 rounded-xl border text-left transition-colors ${
                        serverType === t.value
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      }`}
                    >
                      <div className={`font-semibold text-sm ${serverType === t.value ? 'text-green-400' : 'text-white'}`}>
                        {t.label}
                      </div>
                      <div className="text-gray-500 text-xs mt-0.5">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment method */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="font-bold text-white">Payment method</h2>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPayMethod('stripe')}
                  className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                    payMethod === 'stripe' ? 'border-green-500 bg-green-500/10' : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <CreditCard className={`w-5 h-5 ${payMethod === 'stripe' ? 'text-green-400' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <div className={`font-semibold text-sm ${payMethod === 'stripe' ? 'text-green-400' : 'text-white'}`}>Card</div>
                    <div className="text-gray-500 text-xs">via Stripe</div>
                  </div>
                </button>

                <button
                  onClick={() => setPayMethod('balance')}
                  className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-colors ${
                    payMethod === 'balance' ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700 hover:border-gray-600'
                  }`}
                >
                  <Wallet className={`w-5 h-5 ${payMethod === 'balance' ? 'text-purple-400' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <div className={`font-semibold text-sm ${payMethod === 'balance' ? 'text-purple-400' : 'text-white'}`}>Balance</div>
                    <div className="text-gray-500 text-xs">
                      {balance !== null ? `€${parseFloat(balance).toFixed(2)} available` : '...'}
                    </div>
                  </div>
                </button>
              </div>

              {/* Balance insufficient warning */}
              {payMethod === 'balance' && balance !== null && parseFloat(balance) < discountedTotal() && (
                <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Insufficient balance. You have <strong>€{parseFloat(balance).toFixed(2)}</strong> but need <strong>€{getPrice().toFixed(2)}</strong>.{' '}
                    <Link to="/balance" className="underline hover:text-yellow-300">Top up here →</Link>
                  </span>
                </div>
              )}

              <button
                onClick={handlePay}
                disabled={paying || (payMethod === 'balance' && balance !== null && parseFloat(balance) < discountedTotal())}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                  paying || (payMethod === 'balance' && balance !== null && parseFloat(balance) < discountedTotal())
                    ? 'opacity-50 cursor-not-allowed bg-gray-700 text-gray-400'
                    : payMethod === 'stripe'
                    ? 'bg-green-500 hover:bg-green-400 text-gray-950 hover:shadow-lg hover:shadow-green-500/30'
                    : 'bg-purple-500 hover:bg-purple-400 text-white'
                }`}
              >
                {paying ? (
                  <><Loader className="w-5 h-5 animate-spin" /> {payMethod === 'balance' ? 'Processing...' : 'Redirecting...'}</>
                ) : payMethod === 'balance' ? (
                  <><Wallet className="w-5 h-5" /> Pay €{discountedTotal().toFixed(2)} with Balance</>
                ) : (
                  <>Pay €{discountedTotal().toFixed(2)} with Card</>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
                <Shield className="w-3.5 h-3.5" />
                {payMethod === 'balance'
                  ? 'Instant payment — deducted from your PlarixMC balance'
                  : 'Secured by Stripe — we never see your card details'
                }
              </div>
            </div>
          </div>

          {/* Right - Order summary */}
          <div className="md:col-span-2">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 sticky top-24">
              <h2 className="font-bold text-white mb-4">Order summary</h2>

              <div className="bg-gray-800/50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-white">{plan.name} Plan</span>
                  <span className="text-green-400 font-bold">${getMonthlyEquivalent()}/mo</span>
                </div>
                <div className="text-xs text-gray-500 mb-3">{BILLING_LABELS[billing]} billing</div>
                {getSavings() && (
                  <div className="text-xs text-green-400 font-semibold mb-3">🎉 {getSavings()}</div>
                )}
                <ul className="space-y-1.5 text-xs text-gray-400">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />{plan.ram_mb / 1024}GB RAM</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />{plan.disk_mb / 1024}GB Storage</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />{plan.player_slots} player slots</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />DDoS protection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />Full panel access</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-400" />24/7 support</li>
                </ul>
              </div>

              {/* Coupon */}
              <div className="border-t border-gray-800 pt-4 mb-2">
                {coupon ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/20 rounded-xl px-3 py-2 text-sm">
                    <span className="text-green-400 font-mono">{coupon.code} · −{coupon.percent_off}%</span>
                    <button onClick={() => { setCoupon(null); setCouponInput(''); }} className="text-gray-500 hover:text-gray-300 text-xs">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="Coupon code"
                      className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-green-500 transition-colors placeholder-gray-600"
                    />
                    <button
                      onClick={applyCoupon}
                      disabled={checkingCoupon || !couponInput.trim()}
                      className="bg-gray-800 hover:bg-gray-700 disabled:opacity-40 border border-gray-700 text-white px-4 rounded-xl text-sm transition-colors"
                    >
                      {checkingCoupon ? '...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-800 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>{BILLING_LABELS[billing]} subtotal</span>
                  <span>€{getPrice().toFixed(2)}</span>
                </div>
                {coupon && (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Coupon ({coupon.code})</span>
                    <span>−€{(getPrice() - discountedTotal()).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Setup fee</span>
                  <span className="text-green-400">Free</span>
                </div>
                <div className="flex justify-between font-bold text-white pt-2 border-t border-gray-800">
                  <span>Total today</span>
                  <span>€{discountedTotal().toFixed(2)}</span>
                </div>

                {billing !== 'monthly' && (
                  <div className="text-xs text-gray-500 text-right">
                    (€{getMonthlyEquivalent()}/mo equivalent)
                  </div>
                )}
              </div>

              <div className="mt-4 bg-green-500/5 border border-green-500/20 rounded-xl p-3 text-xs text-gray-400">
                🛡️ 24-hour money-back guarantee. Cancel anytime, no questions asked.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderPage;
